import { useQuery } from "react-query";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { Post, PostSchema } from "./postSchema";
import { QueryClient } from "react-query";
import { getOrganizationPostApiKey } from "src/api/client-api/post/useGetOrganizationPost";
import { getPackagingPostKey } from "src/api/client-api/post/useGetPackagingPost";
import { getProductPostKey } from "src/api/client-api/post/useGetProductPost";
import { getIngredientPostKey } from "src/api/client-api/post/useGetIngredientPost";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";

type Options = {
    postId: number | undefined;
    params?: AxiosParams;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
};

export const getOnePostKey = "getOnePost" as const;

export const useGetOnePost = (options: Options) => {
    const { postId, params = {}, scoresQuery, enabled = true } = options;

    const keyParams = Object.values(params);
    const query = useQuery({
        cacheTime: 0,
        queryKey: [getOnePostKey, postId, ...keyParams, scoresQuery],
        enabled: postId !== undefined && enabled,
        queryFn: async () => {
            const url = `/api/ssq-posts/${postId}`;
            return loggedInApiCall.get(url, {
                headers: {
                    Accept: "application/json",
                },
                params: {
                    ...params,
                    ...scoresQuery,
                },
            });
        },
        select: (req) => {
            const post = req.data;
            const data = zodParseWithDetailedLog(post, GetOnePostSchema, {
                operation: "GET",
                endpoint: "/ssq-posts/:id",
            });

            return data;
        },
    });

    return {
        post: query.data,
        ...query,
    };
};

export const invalidateEntitiesPosts = (queryClient: QueryClient) => {
    queryClient.invalidateQueries([getProductPostKey]);
    queryClient.invalidateQueries([getIngredientPostKey]);
    queryClient.invalidateQueries([getPackagingPostKey]);
    queryClient.invalidateQueries([getOrganizationPostApiKey]);
};

export type GetOnePost = Pick<Post, "id" | "elements" | "campaign" | "ssqVersionId">;

const GetOnePostSchema: z.ZodType<GetOnePost> = PostSchema.pick({
    id: true,
    elements: true,
    campaign: true,
    ssqVersionId: true,
});
